import React, { useRef } from 'react';
import { flexRender } from '@tanstack/react-table';
import cn from '@/utils/cn';
import Text from '@/components/ui/Text';
import Tooltip from '@/components/ui/Tooltip';
import { useCellWidth, useReadingAssignmentTable } from './utils';

export function ReadingAssignmentsTable() {
  const table = useReadingAssignmentTable();
  const ref = useRef<HTMLDivElement>();
  const cellWidth = useCellWidth(ref);

  return (
    <div
      ref={ref}
      className="tw-select-text tw-max-w-full tw-px-4 tw-pb-4 tw-mt-4 tw-max-h-[80%] tw-overflow-auto tw-box-border"
    >
      <table
        className={cn(
          'tw-border tw-border-solid tw-border-light-grey tw-box-border tw-w-full tw-table-fixed',
        )}
      >
        <tbody>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id} className="tw-bg-light-grey tw-sticky tw-z-20 tw-top-0 tw-left-0">
              {headerGroup.headers.map((header, i) => (
                <th
                  key={header.id}
                  className={cn(
                    'tw-px-3 tw-py-1.5 tw-whitespace-nowrap tw-box-border', {
                      'tw-sticky tw-bg-inherit tw-z-20 tw-top-0': i === 0 || i === 1,
                      '-tw-left-5': i === 0,
                    },
                  )}
                  style={{
                    width: cellWidth,
                    ...(i === 1 && cellWidth ? { left: `calc(${cellWidth} - 1.25rem)` } : {}),
                  }}
                >
                  <Tooltip.Provider>
                    <Tooltip>
                      <Tooltip.Portal>
                        <Tooltip.Content
                          className="tw-z-9999"
                          theme="white"
                        >
                          {header.column.columnDef.header}
                          <Tooltip.Arrow />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                      <Tooltip.Trigger asChild>
                        <div>
                          <Text
                            className="tw-max-w-full tw-text-ellipsis tw-overflow-hidden tw-block"
                            tag="span"
                            weight="bold"
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </Text>
                        </div>
                      </Tooltip.Trigger>
                    </Tooltip>
                  </Tooltip.Provider>
                </th>
              ))}
            </tr>
          ))}
          {table.getRowModel().rows.map(row => (
            <tr
              key={row.id}
              className={cn('tw-relative odd:tw-bg-light-grey even:tw-bg-white')}
            >
              {row.getVisibleCells().map((cell, i, cells) => (
                <td
                  key={cell.id}
                  className={cn(
                    'tw-relative hover:tw-bg-[#e0d9ed] tw-transition-colors', {
                      '-tw-left-5': i === 0,
                      'after:-tw-translate-y-1/2 after:tw-w-px after:tw-h-4/6 ': (
                        i !== 0 && i !== 1 && i !== cells.length - 1
                      ),
                      'after:tw-bg-tci-black/15 after:tw-top-1/2': i !== 0 && i !== 1 && i !== cells.length - 1,
                      'after:tw-content-[""] after:tw-absolute after:tw-right-0 ': (
                        i !== 0 && i !== 1 && i !== cells.length - 1
                      ),
                      'tw-sticky tw-bg-inherit tw-z-10': i === 0 || i === 1,
                    },
                  )}
                  style={{
                    ...(i === 1 && cellWidth ? { left: `calc(${cellWidth} - 1.25rem)` } : {}),
                    ...(i === 1 ? { filter: 'drop-shadow(4px 0 4px rgba(0,0,0,0.35))' } : {}),
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, { ...cell.getContext() })}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
